import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save } from 'lucide-react';
import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { Card, CardHeader, CardContent } from '@components/Card';
import { orderStatusService } from '@services/orderStatusService';
import { OrderStatusFormData } from '@/types/orderStatus';

export const OrderStatusFormView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [formData, setFormData] = useState<OrderStatusFormData>({
    order_status: '',
    can_user_choose_manually: true,
    block_order_change: false,
    allocate_available_stock: false,
  });

  useEffect(() => {
    if (isEditing && id) {
      loadData(parseInt(id));
    }
  }, [id, isEditing]);

  const loadData = async (itemId: number) => {
    try {
      setLoading(true);
      setError(null);
      const data = await orderStatusService.getById(itemId);
      setFormData({
        order_status: data.order_status,
        can_user_choose_manually: data.can_user_choose_manually,
        block_order_change: data.block_order_change,
        allocate_available_stock: data.allocate_available_stock,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const updateField = (field: keyof OrderStatusFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.order_status.trim()) {
      newErrors.order_status = 'Nome do status é obrigatório';
    } else if (formData.order_status.length > 20) {
      newErrors.order_status = 'Nome deve ter no máximo 20 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      setError(null);

      if (isEditing && id) {
        await orderStatusService.update(parseInt(id), formData);
      } else {
        await orderStatusService.create(formData);
      }

      navigate('/settings/sales/order-statuses');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="secondary" onClick={() => navigate('/settings/sales/order-statuses')}>
            <ArrowLeft size={20} />
          </Button>
          <div>
            <div className="h-8 bg-slate-200 rounded w-48 animate-pulse" />
            <div className="h-4 bg-slate-200 rounded w-32 mt-2 animate-pulse" />
          </div>
        </div>
        <Card>
          <CardContent>
            <div className="space-y-4">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-14 bg-slate-200 rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate('/settings/sales/order-statuses')}>
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-h1">{isEditing ? 'Editar Status do Pedido' : 'Novo Status do Pedido'}</h1>
          <p className="text-slate-600">
            {isEditing ? 'Altere os dados do status' : 'Preencha os dados para criar um novo status'}
          </p>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <Card className="bg-red-50 border-red-200">
          <CardContent>
            <p className="text-red-600">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Formulário */}
      <Card>
        <CardHeader title="Dados do Status" />
        <CardContent>
          <div className="space-y-6">
            {/* Nome */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Nome do Status <span className="text-red-500">*</span>
              </label>
              <Input
                value={formData.order_status}
                onChange={(e) => updateField('order_status', e.target.value)}
                placeholder="Ex: Em Aberto, Em Andamento, Cancelado"
                maxLength={20}
              />
              {errors.order_status && (
                <p className="text-sm text-red-500 mt-1">{errors.order_status}</p>
              )}
              <p className="text-xs text-slate-500 mt-1">{formData.order_status.length}/20 caracteres</p>
            </div>

            {/* Selecionável Manualmente */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="can_user_choose_manually"
                checked={formData.can_user_choose_manually}
                onChange={(e) => updateField('can_user_choose_manually', e.target.checked)}
                className="h-4 w-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
              />
              <label htmlFor="can_user_choose_manually" className="text-sm font-medium text-slate-700">
                Pode ser selecionado manualmente pelo usuário
              </label>
            </div>

            {/* Bloqueia alteração do pedido */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="block_order_change"
                checked={formData.block_order_change}
                onChange={(e) => updateField('block_order_change', e.target.checked)}
                className="h-4 w-4 text-red-600 border-slate-300 rounded focus:ring-red-500"
              />
              <div>
                <label htmlFor="block_order_change" className="text-sm font-medium text-slate-700">
                  Bloqueia alteração do pedido
                </label>
                <p className="text-xs text-slate-500">Quando marcado, pedidos com esse status não poderão ser editados</p>
              </div>
            </div>

            {/* Reserva estoque disponível */}
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="allocate_available_stock"
                checked={formData.allocate_available_stock}
                onChange={(e) => updateField('allocate_available_stock', e.target.checked)}
                className="h-4 w-4 text-amber-600 border-slate-300 rounded focus:ring-amber-500"
              />
              <div>
                <label htmlFor="allocate_available_stock" className="text-sm font-medium text-slate-700">
                  Reservar estoque disponível
                </label>
                <p className="text-xs text-slate-500">
                  Quando marcado, pedidos neste status reservam o estoque disponível dos produtos (StockQuantityAvailable)
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Ações */}
      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={() => navigate('/settings/sales/order-statuses')}>
          Cancelar
        </Button>
        <Button onClick={handleSave} loading={saving} icon={<Save size={18} />}>
          {isEditing ? 'Salvar Alterações' : 'Criar Status'}
        </Button>
      </div>
    </div>
  );
};
